import { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material/styles";
import {
    Box,
    Grid,
    GridProps,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { Layout, NavigationTypes } from "../Layout";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { getThumbnailUrl } from "JS/React/Hooks/Common/ImageUpload";
import { MediaPlayer } from "JS/React/Components/MediaPlayer";
import { GraphicEq as GraphicEqIcon } from "@mui/icons-material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ContentMedia, MiniPlayerOptions } from "JS/Models/Common";
import {
    ContentType,
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
} from "JS/Models";
import {
    useAudioDetails,
    useGlobalAudioDetails,
    useGlobalAudios,
} from "JS/React/Hooks/Audio";
import {
    useGlobalGifts,
    useReceivedMemberAudioGifts,
} from "JS/React/Hooks/Gifts";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import qs from "qs";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import { useRouting } from "JS/React/Hooks/Routes";
import { GiftCategories } from "../Gifts";
import { FavoritesTypes } from "../Favorites";
import { config } from "JS/Config";
import { getDexieConnectionRefresh } from "JS/Database/Dexie";
import {
    useContentPlayingHandlers,
    usePlayerHandlers,
} from "JS/React/Hooks/MediaPlayer";
import { useMiniPlayerHandlers } from "JS/React/Hooks/MiniPlayer";
import { PlayerActionBtns } from "JS/React/Components/MediaPlayer/PlayerActionBtns";
import { useResumeRecentlyBundles } from "JS/React/Hooks/ResumeRecently";
import { getCurrentUrl } from "JS/Helpers/MiniPlayerHelper";
import { PLAYER_PROGRESS_INTERVAL } from "JS/Helpers/Constants";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";
import {
    SentGiftType,
    getPlayerPlayedTimeForBundle,
    toSentGift,
} from "JS/Models/Firebase/GiftDashboard";

export interface BundlePlayerProps extends InternalStandardProps<GridProps> {}

export const BundlePlayer = (props: BundlePlayerProps) => {
    const { className } = props;
    const params: any = useParams();
    const { contentId } = params;
    const [mediaURL, setMediaURL] = useState("");
    const { linkProvider } = useRouting();
    const location = useLocation();
    const { homeStack } = useGlobalNavStack();

    const [downloadedAudioUrl, setDownloadedAudioUrl] = useState<string>("");
    const [isAudioDownloaded, setIsAudioDownloaded] = useState<boolean>(false);

    const [currentPlayingState, setCurrentPlayingState] = useState(true);
    const [playingState, setPlayingState] = useState(false);
    const { handlePlayerPause, handlePlayerPlay } = useContentPlayingHandlers(
        (val) => {
            setCurrentPlayingState(val);
            setPlayingState(val);
        },
    );
    const { miniPlayer, onClickMiniPlayer } = useMiniPlayerHandlers();

    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const {
            isGift = false,
            index = 0,
            categoryId = 0,
            isMixedContent = "false",
            scrollTo = 0,
            filePath = "",
        } = parsedQueryString;

        return {
            isGift: isGift.toString(),
            index: index.toString(),
            categoryId: categoryId.toString(),
            isMixedContent: isMixedContent,
            scrollTo: scrollTo,
            filePath: filePath.toString(),
        };
    }, [location?.search]);

    const mediaIndex = +queryParams.index;
    const navigate = useNavigate();
    const setQueryParams = (params: Partial<typeof queryParams>) => {
        navigate(
            {
                pathname: location.pathname,
                search: qs.stringify({ ...queryParams, ...params }),
            },
            { replace: true }
        );
    };

    const { audios } = useGlobalAudios();
    const { audioDetails } = useGlobalAudioDetails(contentId);
    const { isGift } = useReceivedMemberAudioGifts(true);
    const { receivedMemberAudios, receivedProspectAudios } = useGlobalGifts();

    const isReceived = () => {
        return queryParams.isGift === "true";
    };

    const audio = useMemo(() => {
        if (isReceived()) {
            const receivedMemberGift = receivedMemberAudios?.find(
                (g) => g?.nid === contentId,
            );
            if (receivedMemberGift)
                return { ...receivedMemberGift, ...audioDetails };
            return {
                ...receivedProspectAudios?.find((g) => g?.nid === contentId),
                ...audioDetails,
            };
        } else
            return {
                ...audios?.find((audio) => audio?.nid === contentId),
                ...audioDetails,
            };
    }, [audios, contentId, receivedMemberAudios, receivedProspectAudios]);

    useAudioDetails(
        contentId,
        "content_item_type_name" in audio
            ? (audio.content_item_type_name as ContentType)
            : "MP3",
    );

    const {
        playerRef,
        setIsReady,
        handleContentCompleted,
        handleRecentlyPlayed,
        onMediaPlayerReady,
        onPlayerProgress,
        isReadyToPlay,
        setIsReadyToPlay,
    } = useResumeRecentlyBundles(audio, mediaIndex);

    const { handleChangeSeek } = usePlayerHandlers(playerRef);

    const isMediaPlayable =
        audio?.isReceived || audio?.is_purchased || audio?.publish_free === "1";
    const media: ContentMedia[] = audio?.media;
    const isBundle = media && media?.length > 1;
    const next = () => {
        setIsReady(false);
        setIsReadyToPlay(false);
        setCurrentPlayingState(true);
        setQueryParams({
            isGift: queryParams.isGift,
            index: ((mediaIndex + 1) % media?.length).toString(),
        });
    };
    const previous = () => {
        setIsReady(false);
        setIsReadyToPlay(false);
        setCurrentPlayingState(true);
        setQueryParams({
            isGift: queryParams.isGift,
            index: (mediaIndex === 0
                ? media?.length - 1
                : mediaIndex - 1
            ).toString(),
        });
    };
    const onComplete = () => {
        if (audio) {
            handleContentCompleted();
            if (isReceivedMemberAudio) {
                const actionsDetails = getDashboardActionDetails();
                logGiftCompleted({ index: actionsDetails.index });
            }
            if (mediaIndex < media?.length - 1) next();
        }
    };
    const { logFirebaseEvent } = useFirebaseLogger();
    const [thumbnail, setThumbnail] = useState<string>(null);

    const fetchThumbnails = async () => {
        if (!thumbnail) {
            const thumb = await getThumbnailUrl(
                audio.image_url_postfix,
                audio.image_file_name,
            );

            setThumbnail(thumb);
        }
    };

    useEffect(() => {
        if (mediaIndex < media?.length && media[mediaIndex]) {
            const { media_url_postfix, media_file_name, media_bucket_name } =
                media[mediaIndex];

            if (!media_bucket_name || !media_url_postfix || !media_file_name) {
                return;
            }

            if (audio) {
                getDexieConnectionRefresh()
                    .audios.get(
                        `${config.user.memberId}-${audio?.nid}-${
                            mediaIndex + 1
                        }`,
                    )
                    .then((res) => {
                        if (res) {
                            if (res.blob) {
                                setIsAudioDownloaded(true);
                                setDownloadedAudioUrl(
                                    URL.createObjectURL(res?.blob),
                                );
                            }
                        } else {
                            setIsAudioDownloaded(false);
                        }
                    })
                    .catch((err) => {
                        setIsAudioDownloaded(false);
                    });

                fetchThumbnails();
                if (media[mediaIndex].media_url) {
                    setMediaURL(media[mediaIndex].media_url);
                }
            }
        }
    }, [audio?.nid, media, mediaIndex]);

    const handleAudioStartedPlaying = () => {
        if (audio) {
            const { nid, sku_id: sku } = audio;
            if (!isAudioDownloaded) {
                const action: FirebaseEventAction = {
                    action: EventActions.LIVE_STREAM,
                    nId: nid,
                    contentTitle: media[mediaIndex].media_title,
                    skuId: sku,
                    category: EventCategories.AUDIOS,
                };

                logFirebaseEvent(EventNames.AUDIO_LIVE_STREAM, action);
            } else {
                const action: FirebaseEventAction = {
                    action: EventActions.PLAY,
                    nId: nid,
                    contentTitle: media[mediaIndex].media_title,
                    skuId: sku,
                    category: EventCategories.AUDIOS,
                };
                logFirebaseEvent(EventNames.AUDIO_PLAYED, action);
            }
            handleRecentlyPlayed();
        }
    };

    const navId =
        isReceived() &&
        (queryParams?.categoryId === GiftCategories.RECEIVED_AS_MEMBER ||
            queryParams?.categoryId === GiftCategories.RECEIVED_AS_PROSPECT)
            ? NavigationTypes.MORE
            : queryParams?.categoryId === FavoritesTypes.FAVAUDIOS
              ? NavigationTypes.MORE
              : NavigationTypes.AUDIOS;

    const { isMixedContent } = queryParams;

    const handleBackNavigation = () => {
        if (!isMixedContent || isMixedContent === "false") {
            if (!homeStack.isHomeTrack) {
                navigate(
                    linkProvider.react
                        .bundle()
                        .audios(
                            contentId,
                            queryParams?.categoryId,
                            queryParams?.isGift,
                            {
                                scrollTo: queryParams?.scrollTo
                                    ? +queryParams?.scrollTo
                                    : 0,
                            },
                        ),
                );
            } else {
                navigate(-1);
            }
        } else {
            navigate(
                linkProvider.react
                    .mixedContent()
                    .bundleDetail(
                        contentId,
                        queryParams?.categoryId,
                        audio?.isReceived.toString(),
                        {
                            isMixedContent: true,
                            scrollTo: queryParams?.scrollTo?.toString(),
                            filePath: queryParams?.filePath,
                        },
                    ),
            );
        }
    };

    const url = isAudioDownloaded ? downloadedAudioUrl : mediaURL;

    const [isLoop, setLoop] = useState(false);

    const miniplayerOptions = useRef<MiniPlayerOptions>(null);
    const alreadyMini = useRef<boolean>(null);

    useEffect(() => {
        if (miniPlayer.url) alreadyMini.current = true;
        else alreadyMini.current = false;
    }, [miniPlayer]);

    useMemo(() => {
        miniplayerOptions.current = {
            mediaUrl: url,
            thumbnail: thumbnail,
            title: media ? media[mediaIndex]?.media_title : "",
            singleLoop: isLoop,
            isPlaying: playingState,
            parentLink: getCurrentUrl(),
            content: audio,
            contentType:
                "content_item_type_name" in audio
                    ? audio?.content_item_type_name
                    : "MP3",
            bundleAudio: {
                type: "online",
                data: audio,
                mediaIndex: mediaIndex,
            },
        };
    }, [url, playingState, thumbnail, isLoop, mediaIndex, media, audio]);

    const miniPlayerClick = () => {
        const options = miniplayerOptions.current;
        if (options)
            onClickMiniPlayer({
                isAudio: true,
                mediaUrl: options.mediaUrl,
                thumbnail: options.thumbnail,
                title: options.title,
                singleLoop: options.singleLoop,
                parentLink: options.parentLink,
                bundleAudio: options.bundleAudio,
                content: options.content,
                contentType: options.contentType,
            })();
    };

    // this useEffect is for component unmounting
    useEffect(() => {
        return () => {
            if (
                miniplayerOptions.current &&
                miniplayerOptions.current.isPlaying &&
                !alreadyMini.current
            ) {
                miniPlayerClick();
            }
        };
    }, []);

    const giftDashboardData = useMemo(() => {
        return toSentGift(audio, mediaIndex);
    }, [audio, mediaIndex]);

    const getDashboardActionDetails = () => {
        return getPlayerPlayedTimeForBundle(playerRef, mediaIndex + 1);
    };
    const isReceivedMemberAudio = isGift(contentId);
    const { logGiftCompleted } = useGiftDashboard(
        giftDashboardData,
        SentGiftType.AUDIO,
        "sender" in audio ? audio?.sender?.member_id : null,
        isReceivedMemberAudio && playingState && !miniPlayer?.url,
        getDashboardActionDetails,
    );

    return (
        <Layout navId={navId} navPath={useLocation().pathname}>
            <AppHeader
                title={media ? media[mediaIndex]?.media_title : ""}
                canGoBack
                searchable={false}
                onBackClick={handleBackNavigation}
            />
            <Root container className={className}>
                {audio && (
                    <>
                        <Grid
                            container
                            xs={12}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            {isMediaPlayable && isBundle ? (
                                <>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                                justifyContent={"space-between"}
                                            >
                                                <ForwardBackItem
                                                    display={"flex"}
                                                    alignItems="center"
                                                    onClick={previous}
                                                >
                                                    <FontAwesomeIcon
                                                        style={{ fontSize: "18px", marginRight: "16px" }}
                                                        icon={[
                                                            "fas",
                                                            "chevron-left",
                                                        ]}
                                                    />
                                                    <FwdBackTypo
                                                        fontWeight={"bold"}
                                                        variant="h5"
                                                    >
                                                        Previous
                                                    </FwdBackTypo>
                                                </ForwardBackItem>
                                                <ForwardBackItem
                                                    display={"flex"}
                                                    alignItems="center"
                                                    onClick={next}
                                                >
                                                    <FwdBackTypo
                                                        fontWeight={"bold"}
                                                        variant="h5"
                                                    >
                                                        Next
                                                    </FwdBackTypo>
                                                    <FontAwesomeIcon
                                                        style={{ fontSize: "18px", marginLeft: "16px" }}
                                                        icon={[
                                                            "fas",
                                                            "chevron-right",
                                                        ]}
                                                    />
                                                </ForwardBackItem>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                    <PlayerContainer
                                        item
                                        xs={12}
                                    >
                                        <MediaPlayer
                                            title={
                                                media
                                                    ? media[mediaIndex]
                                                          ?.media_title
                                                    : ""
                                            }
                                            playerRef={playerRef}
                                            progressInterval={
                                                PLAYER_PROGRESS_INTERVAL
                                            }
                                            onProgress={onPlayerProgress}
                                            onStart={handleAudioStartedPlaying}
                                            onReady={() => onMediaPlayerReady()}
                                            onUpdateExpiredUrl={() => {
                                                setIsReadyToPlay(true);
                                                setIsReady(false);
                                            }}
                                            mediaURL={url}
                                            imageURL={thumbnail}
                                            onEnded={onComplete}
                                            loop={isLoop}
                                            isAudio={true}
                                            playing={
                                                currentPlayingState &&
                                                !miniPlayer.url
                                            }
                                            onNext={next}
                                            onPrevious={previous}
                                            onPlay={handlePlayerPlay}
                                            onPause={handlePlayerPause}
                                            loading={!isReadyToPlay}
                                            onError={() =>
                                                setIsReadyToPlay(true)
                                            }
                                        />
                                    </PlayerContainer>

                                    <Box
                                        display={"flex"}
                                        width={"100%"}
                                        justifyContent={"center"}
                                    >
                                        <PlayerActionBtns
                                            handleChangeSeek={handleChangeSeek}
                                            onClickLoop={() => setLoop(!isLoop)}
                                            isLoop={isLoop}
                                            onClickMiniPlayer={miniPlayerClick}
                                        />
                                    </Box>
                                </>
                            ) : thumbnail ? (
                                <MediaImage
                                    alt={audio?.title}
                                    src={thumbnail}
                                />
                            ) : (
                                <DummyImageBox
                                    display={"flex"}
                                    alignItems={"center"}
                                    justifyContent={"flex-end"}
                                >
                                    <GraphicWrapper>
                                        <GraphicEqIcon
                                            sx={{
                                                fontSize: "60px",
                                                color: "grey.400",
                                            }}
                                        />
                                    </GraphicWrapper>
                                </DummyImageBox>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <Grey800Text variant="body2">
                                {audio.description}
                            </Grey800Text>
                        </Grid>
                    </>
                )}
            </Root>
        </Layout>
    );
};

const Root = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const Grey800Text = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[800],
}));

const PlayerContainer = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    height: "60vh",
    [theme.breakpoints.down("lg")]: {
        height: "50vh",
    },
    [theme.breakpoints.down("sm")]: {
        height: "30vh",
    },
}));

const GraphicWrapper = styled("span")(({ theme }) => ({
    border: `5px solid ${theme.palette.common.white}`,
    borderRadius: "50%",
    height: "120px",
    width: "120px",
    marginRight: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));

const DummyImageBox = styled(Box)(({ theme }) => ({
    position: "relative",
    background: theme.palette.grey[900],
    height: "100%",
}));

const MediaImage = styled("img")(({ theme }) => ({
    width: "100%",
    height: "100%",
    objectFit: "contain",
    background: theme.palette.common.black,
}));

const ForwardBackItem = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    color: theme.palette.grey.A100,
    cursor: "pointer",
}));

const FwdBackTypo = styled(Typography)({
    fontSize: "18px",
});

const FwdBackIcon = styled("span")({
    fontSize: "18px",
});

const BackIcon = styled(FwdBackIcon)(({ theme }) => ({
    marginRight: theme.spacing(2),
}));

const FwdIcon = styled(FwdBackIcon)(({ theme }) => ({
    marginLeft: theme.spacing(2),
}));
